import { type LimitedPartnerFundBreakdownTotals } from "@vega-api/generated/types/limitedPartnerFundBreakdownTotals";
import { type Uuid } from "@vega-api/generated/types/uuid";
import { type DataGridColDef } from "@vega-core/components/DataGrid/types";
import { EMPTY_CELL_VALUE } from "@vega-core/constants/global";
import { m } from "@vega-core/paraglide/messages";
import { formatMoneyShort, isMoneyValid } from "@vega-core/utils/formatMoney";
import { formatMoic, Rounding } from "@vega-core/utils/formatNumber";

import { compareTextCaseInsensitive } from "../utils/compareTextCaseInsensitive";

// The grid renders both per-fund rows and the pinned BE-provided totals row.
// `LimitedPartnerFundBreakdown` (all fields required) is assignable to this; the
// totals row reuses `LimitedPartnerFundBreakdownTotals` (all optional) plus the
// `fundName` label. Formatters therefore guard every value.
export type FundBreakdownGridRow = LimitedPartnerFundBreakdownTotals & {
  fundId?: Uuid;
  fundName: string;
};

export const useFundBreakdownColumns = () => {
  const columns: DataGridColDef<FundBreakdownGridRow>[] = [
    {
      colId: "fundName",
      headerName: m.coInvestments_insights_col_fund(),
      field: "fundName",
      comparator: compareTextCaseInsensitive,
      minWidth: 240,
      pinned: "left",
      flex: 1,
    },
    {
      colId: "primaryCommitment",
      headerName: m.coInvestments_insights_col_primaryCommitment(),
      valueGetter: ({ data }) => data?.primaryCommitment?.amount,
      valueFormatter: ({ data }) =>
        isMoneyValid(data?.primaryCommitment)
          ? formatMoneyShort(data.primaryCommitment)
          : EMPTY_CELL_VALUE,
    },
    {
      colId: "coinvestments",
      headerName: m.coInvestments_insights_col_coinvestments(),
      valueGetter: ({ data }) => data?.coinvestments,
      valueFormatter: ({ data }) =>
        data?.coinvestments != null
          ? String(data.coinvestments)
          : EMPTY_CELL_VALUE,
    },
    {
      colId: "coinvestmentTotal",
      headerName: m.coInvestments_insights_col_coinvestmentTotal(),
      valueGetter: ({ data }) => data?.coinvestmentTotal?.amount,
      valueFormatter: ({ data }) =>
        isMoneyValid(data?.coinvestmentTotal)
          ? formatMoneyShort(data.coinvestmentTotal)
          : EMPTY_CELL_VALUE,
    },
    {
      colId: "coinvestToPrimaryRatio",
      headerName: m.coInvestments_insights_col_coinvestToPrimaryRatio(),
      valueGetter: ({ data }) => data?.coinvestToPrimaryRatio,
      valueFormatter: ({ data }) =>
        data?.coinvestToPrimaryRatio != null
          ? formatMoic(data.coinvestToPrimaryRatio, Rounding.Always2Fractions)
          : EMPTY_CELL_VALUE,
    },
    {
      colId: "dealsShared",
      headerName: m.coInvestments_insights_col_dealsShared(),
      valueGetter: ({ data }) => data?.dealsShared,
      valueFormatter: ({ data }) =>
        data?.dealsShared != null ? String(data.dealsShared) : EMPTY_CELL_VALUE,
    },
  ];

  return columns;
};
